
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList, Text } from 'recharts';
import { Typography } from '@mui/material';

const ORDER_DEFINITIONS = {
  age_range: ["18-24", "25-34", "35-44", "45-54", "55-64", "65+"],
  gender: ["Man", "Woman"],
  employment_status: ["Independent", "Part-time", "Full-time", "Unemployed"],
  income: [ "<10000", "10000-19999", "20000-29999", "30000-39999", "40000-49999", "50000-99999", "100000-199999", ">200000", ],
  education: [ "Elementary", "Low High school", "MBO/ High school", "HBO/WO Bachelor", "Master or PHD", ],
  household_situation: [ "Kids living at home", "Living alone", "Living together without kids unmarried", "Living together married without kids", "Living together with kids unmarried", "Living together married with kids", "Parent living alone", "Other", "Person in institutional housing", ],
  marital_status: ["Unmarried", "Married", "Widowed", "Divorced"],
};

const predefinedColorMap = {
    Woman: '#DF82A0', 
    Man: '#5A9BD4', 
  };

const colors = [
  '#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#8dd1e1',
  '#d0ed57', '#a4de6c', '#d8854f', '#e38690', '#5ABBD4'
];

let colorIndex = 0;
const colorMap = { ...predefinedColorMap};

const getColor = (key) => {
  if (colorMap[key]) {
    return colorMap[key];
  }
  if (!colorMap[key]) {
    colorMap[key] = colors[colorIndex % colors.length];
    colorIndex += 1;
  }
  return colorMap[key];
};

const CustomAxisTick = ({ x, y, payload }) => {
  const parts = payload.value.split(/(-|\s+)/).filter(Boolean);
  const maxLength = 9; 
  const lines = parts.reduce((acc, part) => {
    const currentLine = acc[acc.length - 1] || '';
    if ((currentLine + part).trim().length <= maxLength) {
      acc[acc.length - 1] = `${currentLine} ${part}`.trim();
    } else {
      acc.push(part.trim());
    }
    return acc;
  }, ['']);

  const yOffset = -(lines.length - 1) + 10;

  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, index) => (
        <Text
          key={index}
          x={0}
          y={yOffset + index * 18}
          dy={12}
          textAnchor="middle"
          fill="#666"
        >
          {line}
        </Text>
      ))}
    </g>
  );
};

const PersonaBarChart = ({
  data,
  selectedAttribute,
  groupBy,
  prepareStackedData,
  formatAttributeLabel,
  height = 500,
}) => {
  // Add safety check
  if (!selectedAttribute || !data[selectedAttribute]) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No data to display. Please select an attribute.
        </Typography>
      </div>
    );
  }
  
  const chartData = prepareStackedData(selectedAttribute, groupBy);

  return (
    <div>
      <Typography
          variant="subtitle1"
          sx={{ fontWeight: 'bold', color: "#649470", mb: 1, textAlign: 'center' }}
      >
          {formatAttributeLabel(selectedAttribute)}
      </Typography>

      <ResponsiveContainer width='100%' height={height}>
          <BarChart
          data={chartData}
          margin={{ top: 50, right: 0, left: 0, bottom: 70 }}
          >
          <XAxis dataKey="name" interval={0} tick={<CustomAxisTick />} />
          {groupBy
              ? <YAxis domain={[0, 'dataMax + 1']} />
              : <YAxis
                  domain={[0, 'dataMax + 5']}
                  tickFormatter={(value) => `${value}%`}
              />
          }

          {groupBy
              ? <Tooltip formatter={(value, name) => [value, name]} />
              : <Tooltip formatter={(value, name, props) => [`${props.payload.count}`, 'Count']} />
          }

          {groupBy ? <Legend layout="horizontal" verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: 30 }} /> : null}

          {groupBy 
              ? [...new Set(chartData.flatMap(Object.keys).filter(key => key !== 'name'))]
                  .sort((a, b) => {
                      const subOrder = ORDER_DEFINITIONS[groupBy] || [];
                      return subOrder.indexOf(a) - subOrder.indexOf(b);
                  })
                  .map((key) => (
                  <Bar key={key} dataKey={key} stackId="a" fill={getColor(key)} name={key} />
                  ))
              : <Bar dataKey="value" fill="#7098B0" name={formatAttributeLabel(selectedAttribute)}>
                  <LabelList dataKey="value" position="top" formatter={(value) => `${value}%`} />
              </Bar>
          }
          </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PersonaBarChart;
